/**
 * CardCheck.tsx · 09_APP · OS-006 · 2026-10-20 · D-0070, v59
 *
 * Shown ONLY when a card had no readable email or could not be read (scans send instantly otherwise, D-0070).
 * Every field is editable. One tap on "Send thank-you" creates or finds the contact (exact email, never by
 * name), writes the dated consent evidence, logs that they met, and sends the SAME connect-card email.
 * Built for a thumb first (hard rule 18): full-width inputs, 16px text so iOS does not zoom, big buttons.
 */

import { useState } from "react";
import { os, type CardScan } from "../lib/os";

type Fields = { first_name: string; last_name: string; email: string; organisation: string; job_title: string; phone: string };

function pick(x: Record<string, unknown> | null, k: string): string {
  const v = x?.[k];
  return typeof v === "string" ? v : "";
}

export function CardCheck({ scan, onDone }: { scan: CardScan & { thumb?: string }; onDone: (msg: string) => void }) {
  const x = scan.extracted;
  const [f, setF] = useState<Fields>({
    first_name: pick(x, "first_name"), last_name: pick(x, "last_name"), email: pick(x, "email"),
    organisation: pick(x, "organisation"), job_title: pick(x, "job_title"), phone: pick(x, "phone"),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const unsure = pick(x, "email_confidence") !== "clear" || !f.email;
  const why = pick(x, "unsure_about");

  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  async function send() {
    setBusy(true); setErr(null);
    try {
      const r = await os.cardSend(scan.id, f);
      const name = r.first_name || r.email;
      if (r.sent) onDone(`${name} is saved${r.is_new ? " as a new contact" : ""}, and your thank-you is on its way.`);
      else onDone(r.why ?? `Saved, but the thank-you did not go: ${r.error ?? "unknown reason"}.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try { await os.cardDiscard(scan.id); onDone("Card removed. Nothing was sent."); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="cardcheck">
      <div className="cc-head">
        {scan.thumb ? <img src={scan.thumb} alt="Card photo" /> : <span className="ph" />}
        <div>
          <strong>{scan.status === "failed" ? "Could not read this card" : "No email on this card"}</strong>
          <span>{scan.status === "failed" ? "Type what you can see and tap Send." : "Type their email and tap Send."}</span>
        </div>
      </div>
      <div className="cc-grid">
        <label>First name<input value={f.first_name} onChange={set("first_name")} autoComplete="off" /></label>
        <label>Last name<input value={f.last_name} onChange={set("last_name")} autoComplete="off" /></label>
        <label className={`wide${unsure ? " unsure" : ""}`}>Email
          <input value={f.email} onChange={set("email")} type="email" inputMode="email" autoCapitalize="none" autoComplete="off" />
          {unsure && <em>{f.email ? `Please double check this email.${why ? " " + why : ""}` : "No email could be read. Type it in."}</em>}
        </label>
        <label>Organization<input value={f.organisation} onChange={set("organisation")} autoComplete="off" /></label>
        <label>Title<input value={f.job_title} onChange={set("job_title")} autoComplete="off" /></label>
        <label className="wide">Phone<input value={f.phone} onChange={set("phone")} type="tel" autoComplete="off" /></label>
      </div>
      {err && <div className="refusebox">{err}</div>}
      <div className="cc-actions">
        <button className="scan-btn" onClick={send} disabled={busy || !f.email.trim()}>
          {busy ? "Sending..." : "Send thank-you"}
        </button>
        <button className="cc-remove" onClick={remove} disabled={busy}>Remove</button>
      </div>
    </div>
  );
}
